import {type BoardColumn, useBoardColumnHeadingLevel, useBoardLabels} from '@github-ui/board-core'
import {VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'

import styles from './BoardColumnHeader.module.css'

/**
 * Which heading element a name renders as. Inside `<Board>`, the default comes from the
 * board's own outline. Left overridable so a frame rendered outside `<Board>` can be
 * given the level that fits the caller's outline.
 */
export type BoardHeadingLevel = 2 | 3 | 4 | 5 | 6

export type BoardColumnHeaderProps = {
  column: BoardColumn
  headingLevel?: BoardHeadingLevel
  /**
   * Drawn before the name: a colour swatch for a label or a status, an avatar for an
   * assignee. A sibling of the heading rather than content inside it, so a long name
   * still wraps against the column's full width.
   *
   * Any flow content is allowed, so a visual built from a `<div>` needs no wrapper of its
   * own to be valid here.
   *
   * Decorative, and hidden from assistive technology: `aria-hidden` covers the whole
   * subtree, so an `alt` or an `aria-label` written inside it is never announced. Put
   * anything the visual conveys into the name beside it as well, and nothing focusable in
   * here -- a control a reader can tab to but is never told about belongs in `trailing`.
   */
  leadingVisual?: ReactNode
  /**
   * The name's content, in place of `column.label`. For a column whose field has richer
   * material than a string -- a label's name carries emoji markup, for instance.
   *
   * It still renders inside the heading, so the level, the count and the wrapping stay
   * this component's. **Say the same thing `column.label` says**: that is what names every
   * cell in the column, and a heading that disagrees with it names one column twice.
   */
  children?: ReactNode
  /** An overflow menu, or anything else that belongs beside the name. */
  trailing?: ReactNode
  className?: string
}

/**
 * A column's name and its count.
 *
 * The name is a real heading, so a board of many columns can be skimmed by heading
 * rather than read through.
 *
 * The count is `column.totalCount` and nothing else. It is the server's number for the
 * whole column: summing the counts of the cells below it would be wrong on a swimlaned
 * board, where those cells page independently and may not all be loaded, and counting
 * the cards it holds would be wrong everywhere.
 *
 * It is drawn as a bare number and announced with the unit it is counting. Beside a
 * heading, "812" is read as belonging to it; announced on its own it is a number with
 * nothing attached, so the reader's copy is the label set's rather than the digits.
 */
export function BoardColumnHeader({
  column,
  headingLevel,
  leadingVisual,
  children,
  trailing,
  className,
}: BoardColumnHeaderProps) {
  const contextLevel = useBoardColumnHeadingLevel()
  const Heading = `h${contextLevel ?? headingLevel ?? 3}` as const
  const labels = useBoardLabels()

  return (
    <div className={clsx(styles.ColumnHeader, className)}>
      {leadingVisual === undefined ? null : (
        <div aria-hidden="true" className={styles.LeadingVisual}>
          {leadingVisual}
        </div>
      )}
      <Heading className={styles.Name}>{children ?? column.label}</Heading>
      {column.totalCount === undefined ? null : (
        <span className={styles.Count}>
          {/*
            The digits are drawn and the wording is announced, nested rather than side by
            side so point-and-read lands on both: a pointer on the badge would otherwise
            find only the hidden node and say nothing. `aria-label` is not an option here,
            because a span carries no role for one to attach to.
          */}
          <span aria-hidden="true">{column.totalCount}</span>
          <VisuallyHidden>{labels.columnCount(column.totalCount)}</VisuallyHidden>
        </span>
      )}
      {trailing === undefined ? null : <span className={styles.Trailing}>{trailing}</span>}
    </div>
  )
}
